import DOMPurify from 'dompurify';
import {Box, Clickable, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat} from 'platform/locale';
import styled from 'styled-components';

import {isEmpty, isNil, not} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {getRandomId, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {Button} from '../../Button/Button';
import {Flag} from '../../Flag/Flag';
import {NotificationItemData} from '../NotificationItemData';
import {NotificationAvatar} from './NotificationAvatar';

type NotificationItemProps = Omit<NotificationItemData, 'onClick'> &
  TestIdProps & {
    onNavigate: (url: string) => void;
    onMarkAsRead?: (id: string) => void;
    onClick?: () => Promise<void> | void;
  };

export function NotificationItem(props: NotificationItemProps) {
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const onClick = async () => {
    not(props.isRead) && props.onMarkAsRead?.(props.id);
    props.url && props.onNavigate(props.url);
    if (isNotNil(props.onClick)) {
      startLoading();
      await props.onClick();
      stopLoading();
    }
  };

  const isMarkAsReadEnabled = isNotNil(props.onMarkAsRead) && !!props.isRead;

  const hasFlags = isNotNil(props.flags) && not(isEmpty(props.flags));

  const hasActions = isNotNil(props.actions) && not(isEmpty(props.actions));

  return (
    <Clickable
      isDisabled={isNil(props.resource) && not(isMarkAsReadEnabled)}
      onClick={onClick}
      data-testid={props['data-testid']}
    >
      <LayoutNotificationBackgroud $isRead={props.isRead}>
        <Box minHeight={18} padding={2} overflow="hidden">
          <HStack spacing={2} justify="stretch">
            <NotificationAvatar {...props} isPending={isLoading} />
            <VStack spacing={2} grow={1}>
              <VStack spacing={1}>
                <Show when={hasFlags}>
                  <HStack spacing={2} align="baseline" justify="space-between">
                    <HStack spacing={1}>
                      {props.flags?.map((flag) => (
                        <Flag key={`${flag.label}-${getRandomId()}`} size="small" {...flag} />
                      ))}
                    </HStack>
                    <UnreadIndicator
                      $isVisible={not(props.isRead)}
                      data-testid={suffixTestId('unreadIndicator', props)}
                    />
                  </HStack>
                </Show>

                <HStack spacing={2} align="baseline" justify="space-between">
                  <Text size="xSmall" color="secondary" inline>
                    {props.title}
                  </Text>
                  <UnreadIndicator
                    $isVisible={not(hasFlags) && not(props.isRead)}
                    data-testid={suffixTestId('unreadIndicator', props)}
                  />
                </HStack>
                <Show when={props.resource}>
                  <Text size="xSmall" color="link">
                    {props.resource}
                  </Text>
                </Show>
                {props.created && (
                  <Text size="xSmall" color="secondary">
                    <DateTimeFormat format="dateTimeMedium" value={props.created} />
                  </Text>
                )}
                <Show when={isNotNilOrEmpty(props.message)}>
                  <Box
                    border="1px solid"
                    borderColor="general.separator"
                    borderRadius="small"
                    padding={2}
                  >
                    <MessageText
                      // eslint-disable-next-line no-restricted-syntax -- Data is sanitized so it's safe to use dangerouslySetInnerHTML
                      dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(props.message ?? '')}}
                    />
                  </Box>
                </Show>
              </VStack>
              <Show when={hasActions}>
                <HStack spacing={2}>
                  {props.actions?.map((action) => (
                    <Button key={`${action.title}-${getRandomId()}`} {...action} />
                  ))}
                </HStack>
              </Show>
            </VStack>
          </HStack>
        </Box>
      </LayoutNotificationBackgroud>
    </Clickable>
  );
}

export const LayoutNotificationBackgroud = styled.div<{$isRead?: boolean}>`
  background-color: ${({theme, $isRead}) =>
    $isRead || $isRead === undefined
      ? theme.colors.general.white
      : theme.colors.palettes.blue[10][100]};
  &:hover {
    background-color: ${({theme}) => theme.colors.palettes.neutral[10][100]};
  }
  border-radius: ${({theme}) => theme.radii.small};
`;

// Text component doesn't support line-clamp
// eslint-disable-next-line eag/no-css-property
const MessageText = styled.div`
  font-size: ${({theme}) => theme.fontSizes.text.xSmall};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const UnreadIndicator = styled.div<{$isVisible: boolean}>`
  width: ${({theme}) => theme.getSize(2)};
  height: ${({theme}) => theme.getSize(2)};
  flex-grow: 0;
  flex-shrink: 0;
  border-radius: ${({theme}) => theme.radii.circular};
  background-color: ${({theme}) => theme.colors.general.accent};
  display: ${({$isVisible}) => ($isVisible ? 'block' : 'none')};
`;
